import { useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { useToast } from '../../../shared/hooks/use-toast';
import { listRoles, type RoleWithPermissions } from '../../roles/roles.api';
import { createAlertRule, setAlertRuleEnabled, updateAlertRule, type AlertRuleView } from '../alerts.api';
import { toRuleInput, type AlertRuleFormValues } from '../policies/alert-rule-form';
import { useAlertRuleList } from './use-alert-pages';

/** `null` is closed, `'new'` is a blank form, a rule is that rule being edited. */
export type RuleEditor = null | 'new' | AlertRuleView;

/** The rules tab: the list from the URL's query, the create/edit drawer, and switching a rule off or on. */
export function useAlertRules() {
  const rules = useAlertRuleList();
  const toast = useToast();
  const [editor, setEditor] = useState<RuleEditor>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [roles, setRoles] = useState<RoleWithPermissions[]>([]);

  useEffect(() => {
    listRoles()
      .then(setRoles)
      .catch(() => setRoles([]));
  }, []);

  function openEditor(next: Exclude<RuleEditor, null>) {
    setSaveError(null);
    setEditor(next);
  }

  /** Returns whether the rule was saved, so the drawer knows to close. A refusal stays inline in the drawer. */
  async function save(values: AlertRuleFormValues): Promise<boolean> {
    setSaving(true);
    setSaveError(null);
    try {
      const editing = editor !== null && editor !== 'new' ? editor : null;
      const input = toRuleInput(values, editing?.notifyUserIds);
      await (editing ? updateAlertRule(editing.id, input) : createAlertRule(input));
      toast.success(`Rule ${values.name} ${editing ? 'updated' : 'created'}`);
      setEditor(null);
      await rules.reload();
      return true;
    } catch (err) {
      setSaveError(
        err instanceof ApiError && err.status === 400 ? 'Check the thresholds and who to tell, then try again.' : "Couldn't save the rule. Try again.",
      );
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function toggle(rule: AlertRuleView) {
    try {
      await setAlertRuleEnabled(rule.id, !rule.isEnabled);
      toast.success(`Rule ${rule.name} ${rule.isEnabled ? 'switched off' : 'switched on'}`);
      await rules.reload();
    } catch {
      toast.error(`Couldn't switch ${rule.name} ${rule.isEnabled ? 'off' : 'on'}. Reload and try again.`);
    }
  }

  return { ...rules, editor, openEditor, closeEditor: () => setEditor(null), saving, saveError, save, toggle, roles };
}
